import { useState, useEffect, useRef, useCallback } from "react";

const VIRTUES = [
  { name: "Dignity",     color: "#7B3FA0", emoji: "👑", desc: "A sense of worthiness and respect. It comes from remembering who we truly are and our purpose for being." },
  { name: "Diligence",   color: "#2E6FA3", emoji: "⚡", desc: "Doing what needs to be done with care, concentration and single-pointed attention. It springs from our desire for excellence." },
  { name: "Discernment", color: "#1A7A5E", emoji: "🔮", desc: "Accessing the wisdom of our intuition to discover what is essential and true. Detaching from our desire to rush decisions." },
  { name: "Empathy",     color: "#C0392B", emoji: "💛", desc: "The ability to put ourselves in another's place and understand their experience. We are deeply present to their thoughts and feelings." },
  { name: "Reflection",  color: "#8E44AD", emoji: "🪷", desc: "Taking time to look inward and understand our thoughts, feelings and actions. Reflection brings clarity and wisdom." },
  { name: "Resilience",  color: "#16697A", emoji: "🌿", desc: "The capacity to recover from difficulties with strength and grace. Rising again after every fall with renewed purpose." },
  { name: "Courage",     color: "#B7410E", emoji: "🔥", desc: "Acting rightly in the face of fear or difficulty. The heart of every virtue, making all others possible." },
  { name: "Gratitude",   color: "#7D6608", emoji: "✨", desc: "The quality of being thankful and appreciative for the gifts in our lives, both large and small." },
  { name: "Compassion",  color: "#1A5276", emoji: "🕊️", desc: "Deep awareness of the suffering of others, coupled with the wish to relieve it through kindness and action." },
  { name: "Integrity",   color: "#145A32", emoji: "⚖️", desc: "Wholeness — aligning our actions with our values. Being honest and having strong moral principles in all we do." },
  { name: "Patience",    color: "#6C3483", emoji: "🌸", desc: "The capacity to accept delay or difficulty without becoming anxious or upset. Trusting in the right timing of things." },
  { name: "Kindness",    color: "#1E8449", emoji: "🌻", desc: "Warmth and generosity of spirit. Acting with care and consideration for the feelings and wellbeing of others." },
];

const METHODS = [
  { id: "tap",    icon: "👆", label: "Triple Tap",  hint: "Tap 3 times fast" },
  { id: "shake",  icon: "📳", label: "Shake",       hint: "Shake your phone" },
  { id: "blow",   icon: "🌬️", label: "Blow",        hint: "Blow on the microphone" },
  { id: "rotate", icon: "🔄", label: "Rotate",      hint: "Switch portrait ↔ landscape" },
];

const SHAKE_THRESHOLD = 13;
const SHAKE_COOLDOWN  = 1200;
const TAP_WINDOW      = 600;
const TAP_COUNT       = 3;
const ROTATE_COOLDOWN = 1500;
const SPIN_DURATION   = 3000;

function randomVirtue(cur) {
  let v;
  do { v = VIRTUES[Math.floor(Math.random() * VIRTUES.length)]; } while (v === cur);
  return v;
}

// ── Wheel ──────────────────────────────────────────────────────────────────
function SpinWheel({ spinning, targetAngle, onDone }) {
  const canvasRef = useRef(null);
  const animRef   = useRef(null);
  const startRef  = useRef(null);
  const fromRef   = useRef(0);
  const onDoneRef = useRef(onDone);
  useEffect(() => { onDoneRef.current = onDone; }, [onDone]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const W = canvas.width, H = canvas.height;
    const cx = W / 2, cy = H / 2, r = W / 2 - 4;
    const arc = (2 * Math.PI) / VIRTUES.length;

    function draw(angle) {
      ctx.clearRect(0, 0, W, H);
      VIRTUES.forEach((v, i) => {
        const start = angle + i * arc;
        ctx.beginPath(); ctx.moveTo(cx, cy);
        ctx.arc(cx, cy, r, start, start + arc); ctx.closePath();
        ctx.fillStyle = v.color; ctx.fill();
        ctx.strokeStyle = "rgba(255,255,255,.25)"; ctx.lineWidth = 1.5; ctx.stroke();
        ctx.save(); ctx.translate(cx, cy); ctx.rotate(start + arc / 2);
        ctx.textAlign = "right"; ctx.fillStyle = "rgba(255,255,255,.95)";
        ctx.font = `bold 10px Georgia,serif`;
        ctx.fillText(v.emoji + " " + v.name, r - 8, 4);
        ctx.restore();
      });
      ctx.beginPath(); ctx.arc(cx, cy, 22, 0, Math.PI * 2);
      ctx.fillStyle = "#fff"; ctx.fill();
      ctx.strokeStyle = "rgba(0,0,0,.1)"; ctx.lineWidth = 2; ctx.stroke();
      ctx.fillStyle = "#7B3FA0"; ctx.font = "bold 12px Georgia";
      ctx.textAlign = "center"; ctx.textBaseline = "middle";
      ctx.fillText("✦", cx, cy);
    }

    if (!spinning) { draw(fromRef.current); return; }
    cancelAnimationFrame(animRef.current);
    startRef.current = null;
    const totalRotation = Math.PI * 2 * 5 + targetAngle - (fromRef.current % (Math.PI * 2));

    function animate(ts) {
      if (!startRef.current) startRef.current = ts;
      const progress = Math.min((ts - startRef.current) / SPIN_DURATION, 1);
      const ease = 1 - Math.pow(1 - progress, 3);
      const current = fromRef.current + totalRotation * ease;
      draw(current);
      if (progress < 1) { animRef.current = requestAnimationFrame(animate); }
      else { fromRef.current = current % (Math.PI * 2); onDoneRef.current(); }
    }
    animRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animRef.current);
  }, [spinning, targetAngle]);

  return (
    <canvas ref={canvasRef} width={260} height={260} style={{
      borderRadius: "50%", display: "block",
      boxShadow: "0 8px 40px rgba(0,0,0,.35), 0 0 0 4px rgba(255,255,255,.15)",
    }} />
  );
}

// ── Main ───────────────────────────────────────────────────────────────────
export default function UniversalCardPage() {
  const [card, setCard]               = useState(null);
  const [spinning, setSpinning]       = useState(false);
  const [targetAngle, setTargetAngle] = useState(0);
  const [flipKey, setFlipKey]         = useState(0);
  const [trigger, setTrigger]         = useState(null);
  const [activeMethod, setActiveMethod] = useState(null);
  const [micState, setMicState]         = useState("idle");
  const [volume, setVolume]             = useState(0);

  const spinningRef    = useRef(false);
  const cardRef        = useRef(null);
  const cooldownRef    = useRef(0);
  const pendingCard    = useRef(null);
  const tapsRef        = useRef([]);
  const lastOrient     = useRef(null);
  const rotateCool     = useRef(0);
  const lastAccel      = useRef({ x: 0, y: 0, z: 0 });
  const lastShake      = useRef(0);
  const micFrameRef    = useRef(null);
  const spinToRef      = useRef(null);
  const activeRef      = useRef(null);
  const micStreamRef   = useRef(null);

  useEffect(() => { spinningRef.current = spinning; }, [spinning]);
  useEffect(() => { cardRef.current = card; }, [card]);
  useEffect(() => { activeRef.current = activeMethod; }, [activeMethod]);

  const spinTo = useCallback((source) => {
    const now = Date.now();
    if (spinningRef.current || now - cooldownRef.current < 1000) return;
    cooldownRef.current = now;
    const next = randomVirtue(cardRef.current);
    pendingCard.current = next;
    const idx = VIRTUES.indexOf(next);
    const arc = (2 * Math.PI) / VIRTUES.length;
    setTargetAngle(-Math.PI / 2 - (idx * arc + arc / 2));
    setSpinning(true);
    setTrigger(source);
  }, []);

  useEffect(() => { spinToRef.current = spinTo; }, [spinTo]);

  const handleSpinDone = useCallback(() => {
    setSpinning(false);
    setCard(pendingCard.current);
    setFlipKey(k => k + 1);
    setTimeout(() => setTrigger(null), 1500);
  }, []);

  const stopMic = useCallback(() => {
    cancelAnimationFrame(micFrameRef.current);
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach(t => t.stop());
      micStreamRef.current = null;
    }
    setMicState("idle");
    setVolume(0);
  }, []);

  const selectMethod = useCallback(async (id) => {
    if (activeRef.current === id) {
      if (id === "blow") stopMic();
      setActiveMethod(null);
      return;
    }
    if (activeRef.current === "blow") stopMic();

    setActiveMethod(id);

    if (id === "blow") {
      try {
        const stream   = await navigator.mediaDevices.getUserMedia({ audio: true });
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        const actx     = new AudioCtx();
        await actx.resume();
        const src      = actx.createMediaStreamSource(stream);
        const analyser = actx.createAnalyser();
        analyser.fftSize = 256;
        src.connect(analyser);
        const data = new Uint8Array(analyser.frequencyBinCount);
        micStreamRef.current = stream;
        setMicState("active");

        let lastBlow = 0;
        const tick = () => {
          analyser.getByteTimeDomainData(data);
          let sum = 0;
          for (let i = 0; i < data.length; i++) {
            const v = (data[i] - 128) / 128; sum += v * v;
          }
          const rms = Math.sqrt(sum / data.length) * 100;
          setVolume(Math.min(100, rms * 4));
          if (rms * 100 > 18 && Date.now() - lastBlow > 1200) {
            lastBlow = Date.now();
            if (activeRef.current === "blow") spinToRef.current?.("blow");
          }
          micFrameRef.current = requestAnimationFrame(tick);
        };
        tick();
      } catch {
        setMicState("denied");
        setActiveMethod(null);
      }
    }

    if (id === "shake" && typeof DeviceMotionEvent?.requestPermission === "function") {
      try {
        const r = await DeviceMotionEvent.requestPermission();
        if (r !== "granted") setActiveMethod(null);
      } catch { setActiveMethod(null); }
    }

    if (id === "rotate" && typeof DeviceOrientationEvent?.requestPermission === "function") {
      try {
        const r = await DeviceOrientationEvent.requestPermission();
        if (r !== "granted") setActiveMethod(null);
      } catch { setActiveMethod(null); }
    }
  }, [stopMic]);

  const handleTap = useCallback((e) => {
    if (e.target.closest("button")) return;
    if (activeRef.current !== "tap") return;
    if (spinningRef.current) return;
    const now = Date.now();
    tapsRef.current = tapsRef.current.filter(t => now - t < TAP_WINDOW);
    tapsRef.current.push(now);
    if (tapsRef.current.length >= TAP_COUNT) {
      tapsRef.current = [];
      spinTo("tap");
    }
  }, [spinTo]);

  useEffect(() => {
    if (typeof DeviceMotionEvent === "undefined") return;
    const handler = (e) => {
      if (activeRef.current !== "shake") return;
      const ag = e.accelerationIncludingGravity;
      if (!ag || ag.x == null) return;
      const dx = Math.abs(ag.x - lastAccel.current.x);
      const dy = Math.abs(ag.y - lastAccel.current.y);
      const dz = Math.abs(ag.z - lastAccel.current.z);
      lastAccel.current = { x: ag.x, y: ag.y, z: ag.z };
      if (dx + dy + dz > SHAKE_THRESHOLD && Date.now() - lastShake.current > SHAKE_COOLDOWN) {
        lastShake.current = Date.now();
        spinToRef.current?.("shake");
      }
    };
    window.addEventListener("devicemotion", handler, { passive: true });
    return () => window.removeEventListener("devicemotion", handler);
  }, []);

  useEffect(() => {
    if (typeof DeviceOrientationEvent === "undefined") return;
    const handler = (e) => {
      if (activeRef.current !== "rotate") return;
      if (e.gamma == null) return;
      const cur = Math.abs(e.gamma) > 30 ? "landscape" : "portrait";
      if (lastOrient.current && lastOrient.current !== cur) {
        const now = Date.now();
        if (now - rotateCool.current > ROTATE_COOLDOWN) {
          rotateCool.current = now;
          spinToRef.current?.("rotate");
        }
      }
      lastOrient.current = cur;
    };
    window.addEventListener("deviceorientation", handler, { passive: true });
    return () => window.removeEventListener("deviceorientation", handler);
  }, []);

  useEffect(() => () => { stopMic(); }, [stopMic]);

  const triggerLabel = {
    shake: "📳 Shake!", tap: "👆 Triple tap!",
    rotate: "🔄 You rotated!", blow: "🌬️ You blew!", button: "🎰 Roulette!",
  };
  const volSegs = Array.from({ length: 8 }, (_, i) => i < Math.round(volume / 12.5));

  return (
    <div onClick={handleTap} style={{
      minHeight: "100vh",
      background: "linear-gradient(160deg,#1a0533 0%,#0d1a2e 60%,#0a1a0f 100%)",
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "flex-start",
      fontFamily: "Georgia,'Times New Roman',serif",
      padding: "20px 16px 32px",
      userSelect: "none", WebkitTapHighlightColor: "transparent", overflowX: "hidden",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700&family=Crimson+Text:ital,wght@0,400;0,600;1,400&display=swap');
        @keyframes revealCard {
          0%   { transform:perspective(800px) rotateY(90deg) scale(.9);opacity:0 }
          100% { transform:perspective(800px) rotateY(0deg) scale(1);opacity:1 }
        }
        @keyframes floatUp {
          0%   { opacity:1;transform:translateY(0) scale(1.1) }
          100% { opacity:0;transform:translateY(-50px) scale(1.3) }
        }
        @keyframes pointer-bounce {
          0%,100% { transform:translateX(-50%) translateY(0) }
          50%     { transform:translateX(-50%) translateY(-5px) }
        }
        @keyframes shimmer-bg {
          0%   { background-position:0% 50% }
          100% { background-position:200% 50% }
        }
        @keyframes spin-glow {
          0%,100% { box-shadow:0 0 20px rgba(123,63,160,.5) }
          50%     { box-shadow:0 0 50px rgba(123,63,160,.9),0 0 80px rgba(123,63,160,.4) }
        }
        @keyframes pulse-active {
          0%,100% { box-shadow:0 0 0 0 rgba(255,215,0,.4) }
          50%     { box-shadow:0 0 0 6px rgba(255,215,0,0) }
        }
        .card-reveal  { animation:revealCard .55s cubic-bezier(.2,0,.2,1) forwards }
        .trigger-pop  { animation:floatUp 1.4s ease forwards }
        .pointer-anim { animation:pointer-bounce 1.2s ease infinite }
        .spin-glow    { animation:spin-glow 1s ease infinite }
        .method-active { animation:pulse-active 2s ease infinite }
      `}</style>

      <div style={{ textAlign: "center", marginBottom: 16, width: "100%" }}>
        <h1 style={{ fontFamily: "'Cinzel',serif", color: "#fff",
          fontSize: "clamp(18px,5vw,26px)", fontWeight: 700, letterSpacing: ".15em", marginBottom: 4 }}>
          ✦ Virtues Roulette ✦
        </h1>
      </div>

      <div style={{ height: 24, marginBottom: 6, display: "flex", alignItems: "center", justifyContent: "center" }}>
        {trigger && (
          <span key={flipKey} className="trigger-pop" style={{
            color: "#ffd700", fontSize: 15, fontWeight: "bold",
            fontFamily: "'Cinzel',serif", letterSpacing: ".06em",
          }}>{triggerLabel[trigger]}</span>
        )}
      </div>

      <div style={{ position: "relative", marginBottom: 16 }}>
        <div className={spinning ? "pointer-anim" : ""} style={{
          position: "absolute", top: -14, left: "50%", transform: "translateX(-50%)",
          width: 0, height: 0,
          borderLeft: "10px solid transparent", borderRight: "10px solid transparent",
          borderTop: "20px solid #ffd700",
          filter: "drop-shadow(0 2px 6px rgba(255,215,0,.6))", zIndex: 10,
        }}/>
        <div className={spinning ? "spin-glow" : ""} style={{ borderRadius: "50%" }}>
          <SpinWheel spinning={spinning} targetAngle={targetAngle} onDone={handleSpinDone} />
        </div>
      </div>

      <button
        onClick={(e) => { e.stopPropagation(); spinTo("button"); }}
        disabled={spinning}
        style={{
          background: spinning ? "rgba(255,255,255,.1)" : "linear-gradient(135deg,#7B3FA0,#C0392B,#7B3FA0)",
          backgroundSize: "200% 200%",
          animation: spinning ? "none" : "shimmer-bg 3s linear infinite",
          border: "none", borderRadius: 50, padding: "13px 40px",
          color: spinning ? "rgba(255,255,255,.4)" : "#fff",
          fontFamily: "'Cinzel',serif", fontSize: 15, fontWeight: 700, letterSpacing: ".15em",
          cursor: spinning ? "not-allowed" : "pointer",
          boxShadow: spinning ? "none" : "0 6px 30px rgba(123,63,160,.5)",
          transition: "all .3s", marginBottom: 20, minWidth: 200,
        }}
      >
        {spinning ? "Spinning…" : "🎰 SPIN"}
      </button>

      <div style={{ width: "100%", maxWidth: 360, marginBottom: 16 }}>
        <p style={{ color: "rgba(255,255,255,.35)", fontSize: 10, letterSpacing: ".1em",
          textTransform: "uppercase", textAlign: "center", marginBottom: 10 }}>
          Choose an interaction method
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {METHODS.map(m => {
            const isActive = activeMethod === m.id;
            const isBlow   = m.id === "blow";
            return (
              <button
                key={m.id}
                onClick={(e) => { e.stopPropagation(); selectMethod(m.id); }}
                className={isActive ? "method-active" : ""}
                style={{
                  background: isActive
                    ? "rgba(255,215,0,.12)"
                    : "rgba(255,255,255,.05)",
                  border: isActive
                    ? "1.5px solid rgba(255,215,0,.6)"
                    : "1.5px solid rgba(255,255,255,.1)",
                  borderRadius: 14, padding: "12px 10px",
                  cursor: "pointer", transition: "all .25s",
                  display: "flex", flexDirection: "column",
                  alignItems: "center", gap: 4,
                }}
              >
                <span style={{ fontSize: 22 }}>{m.icon}</span>
                <span style={{
                  fontFamily: "'Cinzel',serif",
                  color: isActive ? "#ffd700" : "rgba(255,255,255,.6)",
                  fontSize: 11, fontWeight: 700, letterSpacing: ".06em",
                  transition: "color .25s",
                }}>{m.label}</span>
                {isBlow && isActive && micState === "active" ? (
                  <span style={{ display: "flex", gap: 2, alignItems: "flex-end", height: 14 }}>
                    {volSegs.map((on, i) => (
                      <span key={i} style={{
                        width: 3, borderRadius: 2, height: 3 + i * 1.3,
                        background: on ? "#67e8f9" : "rgba(255,255,255,.15)",
                        display: "block", transition: "background .1s",
                      }}/>
                    ))}
                  </span>
                ) : (
                  <span style={{
                    color: isActive ? "rgba(255,215,0,.55)" : "rgba(255,255,255,.25)",
                    fontSize: 10, transition: "color .25s",
                  }}>{m.hint}</span>
                )}
                {isBlow && micState === "denied" && (
                  <span style={{ color: "#fc8181", fontSize: 10 }}>Blocked</span>
                )}
              </button>
            );
          })}
        </div>
        {activeMethod && (
          <div style={{
            marginTop: 10, textAlign: "center",
            color: "rgba(255,215,0,.7)", fontSize: 11,
            letterSpacing: ".06em",
          }}>
            {METHODS.find(m => m.id === activeMethod)?.icon}&nbsp;
            <strong>{METHODS.find(m => m.id === activeMethod)?.label}</strong> active
            &nbsp;·&nbsp;
            <span
              onClick={(e) => { e.stopPropagation(); if (activeMethod === "blow") stopMic(); setActiveMethod(null); }}
              style={{ color: "rgba(255,255,255,.3)", cursor: "pointer", textDecoration: "underline", fontSize: 10 }}
            >disable</span>
          </div>
        )}
      </div>

      {card && !spinning && (
        <div key={flipKey} className="card-reveal" style={{
          background: `linear-gradient(145deg,${card.color}22,${card.color}11)`,
          border: `2px solid ${card.color}66`, borderRadius: 20,
          padding: "20px 22px", width: "100%", maxWidth: 360,
          boxShadow: `0 12px 50px ${card.color}33,0 0 0 1px ${card.color}22`,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
            <span style={{ fontSize: 32, lineHeight: 1 }}>{card.emoji}</span>
            <h2 style={{ fontFamily: "'Cinzel',serif", color: "#fff",
              fontSize: "clamp(18px,5vw,24px)", fontWeight: 700, letterSpacing: ".08em" }}>
              {card.name}
            </h2>
          </div>
          <div style={{ width: 40, height: 2, background: card.color, borderRadius: 2, marginBottom: 12, opacity: .8 }}/>
          <p style={{ fontFamily: "'Crimson Text',Georgia,serif", color: "rgba(255,255,255,.75)",
            fontSize: "clamp(14px,3.5vw,16px)", lineHeight: 1.65, fontStyle: "italic" }}>
            {card.desc}
          </p>
        </div>
      )}
    </div>
  );
}
